export type ExportRowHighlightColor = 'amber' | 'sky' | 'emerald' | 'rose' | 'violet';

export type ExportRowHighlightMap = Record<string, ExportRowHighlightColor>;

export interface ExportRowHighlightOption {
  id: ExportRowHighlightColor;
  label: string;
  main: string;
  shortcutKey: string;
}

export interface ExportRowHighlightStorageScope {
  storageKey: string;
  documentPart: string;
}

export const EXPORT_ROW_HIGHLIGHT_OPTIONS: ExportRowHighlightOption[] = [
  { id: 'amber', label: 'amber', main: '#d97706', shortcutKey: '1' },
  { id: 'sky', label: 'blue', main: '#0284c7', shortcutKey: '2' },
  { id: 'emerald', label: 'green', main: '#059669', shortcutKey: '3' },
  { id: 'rose', label: 'rose', main: '#e11d48', shortcutKey: '4' },
  { id: 'violet', label: 'violet', main: '#7c3aed', shortcutKey: '5' },
];

const StoragePrefix = 'pipeex-export-row-highlights:v1';
const ValidHighlightColors = new Set<ExportRowHighlightColor>(
  EXPORT_ROW_HIGHLIGHT_OPTIONS.map((option) => option.id)
);

function getStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

function normalizeStoragePart(value: string | null | undefined, fallback: string): string {
  const normalized = value?.trim().replace(/\//g, '\\').toLowerCase();
  return encodeURIComponent(normalized || fallback);
}

export function getExportRowHighlightOption(
  color: ExportRowHighlightColor | null | undefined
): ExportRowHighlightOption | null {
  return EXPORT_ROW_HIGHLIGHT_OPTIONS.find((option) => option.id === color) ?? null;
}

export function buildExportRowHighlightStorageScope(input: {
  projectKey: string | null;
}): ExportRowHighlightStorageScope {
  const projectPart = normalizeStoragePart(input.projectKey, 'no-document');

  return {
    storageKey: `${StoragePrefix}:document:${projectPart}`,
    documentPart: projectPart,
  };
}

export function loadExportRowHighlightMap(scope: ExportRowHighlightStorageScope): ExportRowHighlightMap {
  const storage = getStorage();
  if (!storage) {
    return {};
  }

  try {
    const parsed = JSON.parse(storage.getItem(scope.storageKey) || '{}') as Record<string, unknown>;
    const next: ExportRowHighlightMap = {};
    Object.entries(parsed).forEach(([systemName, color]) => {
      const normalizedSystemName = systemName.trim();
      if (!normalizedSystemName || !ValidHighlightColors.has(color as ExportRowHighlightColor)) {
        return;
      }

      next[normalizedSystemName] = color as ExportRowHighlightColor;
    });
    return next;
  } catch {
    return {};
  }
}

export function saveExportRowHighlightMap(
  scope: ExportRowHighlightStorageScope,
  highlights: ExportRowHighlightMap
): void {
  const storage = getStorage();
  if (!storage) {
    return;
  }

  try {
    if (Object.keys(highlights).length === 0) {
      storage.removeItem(scope.storageKey);
      return;
    }

    storage.setItem(scope.storageKey, JSON.stringify(highlights));
  } catch {
    // Ignore quota/private-mode failures. Row colors are a review aid, not source data.
  }
}

export function setExportRowHighlightColor(
  previous: ExportRowHighlightMap,
  systemName: string,
  color: ExportRowHighlightColor | null
): ExportRowHighlightMap {
  const normalizedSystemName = systemName.trim();
  if (!normalizedSystemName) {
    return previous;
  }

  const next = { ...previous };
  if (!color) {
    delete next[normalizedSystemName];
    return next;
  }

  next[normalizedSystemName] = color;
  return next;
}
